import { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import axios from 'axios';
import { NumberStepper } from './NumberStepper';
import { generateLabels } from '../services/PickingCRUD';
import { printLabelFromUrl } from '../utils/printLabel';
import { printViaPrintAgent } from '../utils/printAgent';

interface LabelPrintingPanelProps {
  cartCode: string;
  orderId: number;
  defaultBoxes: number;
  onPrinted: () => void;
  onLabelGenerated?: (labelUrl: string) => void;
}

const LabelPrintingPanel: FC<LabelPrintingPanelProps> = ({
  cartCode,
  orderId,
  defaultBoxes,
  onPrinted,
  onLabelGenerated,
}) => {
  const [nPackages, setNPackages] = useState(defaultBoxes);
  const [printing, setPrinting] = useState(false);
  const intl = useIntl();

  const handlePrint = async () => {
    setPrinting(true);
    try {
      const { data } = await generateLabels(cartCode, orderId, nPackages);
      if (data.label_url) {
        const fullUrl = `${process.env.REACT_APP_API_URL}${data.label_url}`;
        const printed = await printViaPrintAgent(fullUrl);
        if (!printed) {
          printLabelFromUrl(fullUrl);
        }
        onLabelGenerated?.(fullUrl);
        toast.success(intl.formatMessage({ id: 'picking.printSuccess' }));
      } else {
        toast.warning(intl.formatMessage({ id: 'picking.labelsNoUrl' }));
      }
      onPrinted();
    } catch (error: unknown) {
      const data = axios.isAxiosError(error) ? error.response?.data : undefined;
      const message =
        data?.detail ||
        data?.non_field_errors?.[0] ||
        intl.formatMessage({ id: 'picking.printError' });
      toast.error(message);
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="card p-4 mt-4 border-primary">
      <h5 className="mb-3">
        {intl.formatMessage({ id: 'picking.printLabels' })}
      </h5>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
        <div>
          <label className="form-label text-muted mb-1">
            {intl.formatMessage({ id: 'picking.nPackages' })}
          </label>
          <NumberStepper value={nPackages} onChange={setNPackages} min={1} />
        </div>
        <button
          className="btn btn-primary btn-lg"
          style={{ minWidth: 200, height: 56, fontSize: 18 }}
          onClick={handlePrint}
          disabled={printing}
        >
          {printing ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              {intl.formatMessage({ id: 'picking.generatingLabels' })}
            </>
          ) : (
            intl.formatMessage({ id: 'picking.printLabels' })
          )}
        </button>
      </div>
    </div>
  );
};

export { LabelPrintingPanel };
